import React, { useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { IntentAnswer } from "@/model";
import { useAgentId, useJSONViewer, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import StraightArrowIcon from "@/component/icon/StraightArrowIcon";


interface IntentPredictionCardProps {
    answer: IntentAnswer,
    defaultOpen: boolean
}

const IntentPredictionCard: React.FC<IntentPredictionCardProps> = (props) => {
    const { answer, defaultOpen } = props;

    const intl = useIntl();
    const agentId = useAgentId();
    const { moveToIntent } = useRouteHandler();

    const [open, setOpen] = useState(defaultOpen);

    const slotKeys = Object.keys(answer.slots);

    const onClickOpenToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        setOpen(!open);
    };

    const onClickMoveIntent = (e: React.MouseEvent) => {
        e.preventDefault();
        moveToIntent(agentId, {
            state: {
                skillId: answer.intent.skill_id,
                intentId: answer.intent.intent_id
            }
        });
    };

    return (
        <div className="single-test-card">
            <div className="pt-0.5 px-1 flex justify-between items-center">
                <div className="single-test-card-head">
                    {answer.intent.intent_name}
                </div>

                <div className="flex items-center gap-1.5">
                    <div className="single-test-card-subhead text-[12px]">
                        {answer.intent.score}
                    </div>

                    <Button onClick={onClickOpenToggle}>
                        <ChevronArrowIcon direction={open ? "up" : "down"}
                                          className="stroke-black-400 size-3.5"
                        />
                    </Button>
                </div>
            </div>

            <div className={classNames(
                     "flex flex-col mt-2 pt-2 border-t border-black-200",
                     !open && "hidden"
                 )}
            >
                <div className="flex flex-col gap-0.5">
                    <div className="single-test-card-subhead">
                        {intl.formatMessage({ id: "i00009" })}
                    </div>
                    <div className="single-test-card-description">
                        {answer.intent.skill_name}
                    </div>
                </div>

                {slotKeys.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1">
                        <div className="single-test-card-subhead flex justify-between">
                            <div>
                                Parameter
                            </div>
                            <div>
                                Value
                            </div>
                        </div>
                        {slotKeys.map(slotKey => (
                            <div key={slotKey}
                                 className="single-test-card-description flex justify-between"
                            >
                                <div>
                                    ${slotKey}
                                </div>
                                <SlotValueFragment slotKey={slotKey}
                                                   slotValue={answer.slots[slotKey]}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <Button className="ml-auto mt-4 flex items-center"
                        onClick={onClickMoveIntent}
                >
                    <div className="single-test-card-subhead text-[12px]">
                        {intl.formatMessage({ id: "i00010" })}
                    </div>
                    <StraightArrowIcon direction="right"
                                       className="stroke-black-400 size-3.5"
                    />
                </Button>
            </div>
        </div>
    );
};

type SlotValueFragmentProps = {
    slotKey: string;
    slotValue: any;
}

const SlotValueFragment: React.FC<SlotValueFragmentProps> = ({ slotKey, slotValue }) => {
    const intl = useIntl();
    const jsonViewer = useJSONViewer();

    if (!(slotValue instanceof Object)) {
        return (
            <div>
                {slotValue}
            </div>
        );
    }
    return (
        <Button size="md"
                className="p-0"
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    jsonViewer.open(`Slot Value: $${slotKey}`, slotValue);
                }}
        >
            {intl.formatMessage({ id: 'i00011' })}
        </Button>
    );
};

export default IntentPredictionCard;
